import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
  formatManualAssessmentInitiateErrorMessage,
  resolveManualAssessmentInitiateError,
} from "../../utils/manualAssessmentErrorMessages";
import { buildInitTasksPayload, buildWorkflowReferences } from "../../utils/manualAssessmentInitTasks";
import { getTenantId } from "../../services/apiClient";
import {
  getManualAssessmentProcessTemplates,
  getManualAssessmentProcessPreInit,
  MANUAL_ASSESSMENT_INITIATE_SUCCESS_TOAST,
  MANUAL_ASSESSMENT_INITIATE_SUCCESS_TOAST_MS,
  MANUAL_ASSESSMENT_NO_PROCESS_TEMPLATES_MESSAGE,
  pickDefaultMainProcessId,
  postTriageProcessesRecommendation,
  resolveProcessTemplateInfo,
  shouldLoadManualAssessmentViaTriage,
  startManualAssessmentWorkflow,
  type ManualProcessSummary,
  type ProcessTemplateGroup,
  type ProcessTemplateInfo,
  type RegulationAssessmentResultPayload,
  type StartManualAssessmentRegulationContext,
  type TriageImpactedItem,
  type TriageProcessesRecommendationData,
} from "../../services/manualAssessmentService";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import noDataProcessMapping from "../../assets/no-data-process-mapping.png";
import ManualAssessmentPreInitDialog from "../RegulationDetails/ManualAssessmentPreInitDialog";
import type { PreInitTask, PreInitWorkflowField, WorkflowStartReference } from "../../types/manualAssessmentWorkflow";
import StartManualAssessmentProcessTable, {
  type ManualAssessmentMainRow,
} from "./StartManualAssessmentProcessTable";

/** How the dialog was opened — selects triage vs process-templates on load. */
export type ManualAssessmentEntrySource = "from_ai_impact" | "direct";

export type StartManualAssessmentDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  regulation: StartManualAssessmentRegulationContext;
  /**
   * `from_ai_impact` — opened from IA (Results modal, Assessment Center, history).
   * `direct` — explicit Start Manual Assessment button (default).
   */
  entrySource?: ManualAssessmentEntrySource;
  /**
   * Structured IA `impacted_items` from poll/history (`impacted_items` field).
   * Triage runs only when `entrySource` is `from_ai_impact` and this is non-empty.
   */
  impactedItems?: readonly TriageImpactedItem[] | null;
  /** Triage path: IA row context for `concept_assessment_result` on initiate. */
  conceptAssessmentResult?: RegulationAssessmentResultPayload | null;
  onInitiated?: () => void;
}>;

function templateGroupsToMainRows(
  groups: readonly ProcessTemplateGroup[],
): ManualAssessmentMainRow[] {
  return groups.map((g) => ({
    id: g.process_template.id,
    name: g.process_template.name,
    description: g.process_template.description || null,
    subProcesses: g.sub_process_templates,
  }));
}

function triageToMainRows(
  triage: TriageProcessesRecommendationData,
): ManualAssessmentMainRow[] {
  return [
    {
      id: triage.main_process.id,
      name: triage.main_process.name,
      description: triage.main_process.description,
      subProcesses: triage.sub_processes ?? [],
    },
  ];
}

/**
 * Process selection + Initiate for Start Manual Assessment.
 * @see docs/requirements/assessment-center/manual-impact-assessment.md
 */
export default function StartManualAssessmentDialog({
  open,
  onClose,
  regulation,
  entrySource = "direct",
  impactedItems,
  conceptAssessmentResult = null,
  onInitiated,
}: StartManualAssessmentDialogProps) {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const [loadFailed, setLoadFailed] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [triage, setTriage] = useState<TriageProcessesRecommendationData | null>(
    null,
  );
  const [templateGroups, setTemplateGroups] = useState<ProcessTemplateGroup[]>(
    [],
  );
  const [selectedSubIds, setSelectedSubIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [selectedMainId, setSelectedMainId] = useState<number | null>(null);
  const [expandedMainIds, setExpandedMainIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const [requiresInit, setRequiresInit] = useState<boolean | null>(null);
  const [preInitFailed, setPreInitFailed] = useState(false);
  const [preInitCheckPending, setPreInitCheckPending] = useState(false);
  const [preInitOpen, setPreInitOpen] = useState(false);
  const [preInitTasks, setPreInitTasks] = useState<PreInitTask[]>([]);
  const [preInitWorkflowFields, setPreInitWorkflowFields] = useState<
    PreInitWorkflowField[]
  >([]);

  const useTriageLoad = shouldLoadManualAssessmentViaTriage(
    entrySource,
    impactedItems,
  );
  const triageRequestItems = useMemo(
    () => (impactedItems ?? []).map((i) => ({ ...i })),
    [impactedItems],
  );
  const impactedKey = JSON.stringify(triageRequestItems);
  const snapshotCacheRef = useRef<{ key: string; items: TriageImpactedItem[] }>({
    key: "",
    items: [],
  });
  if (snapshotCacheRef.current.key !== impactedKey) {
    snapshotCacheRef.current = {
      key: impactedKey,
      items: triageRequestItems,
    };
  }
  const itemsSnapshot = snapshotCacheRef.current.items;

  const resetLocalState = useCallback(() => {
    setLoadFailed(false);
    setInitialLoadComplete(false);
    setSubmitting(false);
    setTriage(null);
    setTemplateGroups([]);
    setSelectedSubIds(new Set());
    setSelectedMainId(null);
    setExpandedMainIds(new Set());
    setRequiresInit(null);
    setPreInitFailed(false);
    setPreInitCheckPending(false);
    setPreInitOpen(false);
    setPreInitTasks([]);
    setPreInitWorkflowFields([]);
  }, []);

  const loadPending = open && !loadFailed && !initialLoadComplete;

  useEffect(() => {
    if (!open) {
      resetLocalState();
      return;
    }

    let cancelled = false;
    setLoadFailed(false);
    setInitialLoadComplete(false);
    setTriage(null);
    setTemplateGroups([]);
    setSelectedSubIds(new Set());
    setSelectedMainId(null);
    setExpandedMainIds(new Set());

    const applyDefaultMainSelection = (groups: ProcessTemplateGroup[]) => {
      const mainId = pickDefaultMainProcessId(groups);
      if (mainId != null) {
        setSelectedMainId(mainId);
        setExpandedMainIds(new Set([mainId]));
      }
    };

    if (useTriageLoad) {
      void Promise.all([
        postTriageProcessesRecommendation({ impacted_items: itemsSnapshot }),
        getManualAssessmentProcessTemplates(),
      ])
        .then(([res, groups]) => {
          if (cancelled) return;
          setTemplateGroups(groups);
          if (res.status !== "success" || !res.data) {
            showMessage(
              res.message ?? "Failed to load process recommendations.",
              "error",
            );
            setLoadFailed(true);
            return;
          }
          const data = res.data;
          setTriage(data);
          const mainId = data.main_process.id;
          setSelectedMainId(mainId);
          setExpandedMainIds(new Set([mainId]));
          const rec = data.recommended_sub_processes ?? [];
          setSelectedSubIds(new Set(rec.map((s) => s.id)));
          setInitialLoadComplete(true);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          showMessage(
            e instanceof Error
              ? e.message
              : "Failed to load process recommendations.",
            "error",
          );
          setLoadFailed(true);
        });
    } else {
      void getManualAssessmentProcessTemplates()
        .then((groups) => {
          if (cancelled) return;
          setTemplateGroups(groups);
          applyDefaultMainSelection(groups);
          setInitialLoadComplete(true);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          showMessage(
            e instanceof Error ? e.message : "Failed to load process templates.",
            "error",
          );
          setLoadFailed(true);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [open, useTriageLoad, impactedKey, itemsSnapshot, resetLocalState, showMessage]);

  const mainRows = useMemo((): ManualAssessmentMainRow[] => {
    if (useTriageLoad && triage) return triageToMainRows(triage);
    if (!useTriageLoad && templateGroups.length > 0) {
      return templateGroupsToMainRows(templateGroups);
    }
    return [];
  }, [useTriageLoad, triage, templateGroups]);

  const selectedMainRow = mainRows.find((r) => r.id === selectedMainId) ?? null;

  const mainProcess: ManualProcessSummary | null = useMemo(() => {
    if (!selectedMainRow) return null;

    const inlineSummary: ManualProcessSummary = {
      id: selectedMainRow.id,
      name: selectedMainRow.name,
      description: selectedMainRow.description,
      definition_id: useTriageLoad
        ? triage?.main_process.definition_id
        : templateGroups.find(
            (g) => g.process_template.id === selectedMainRow.id,
          )?.process_template.definition_id,
    };

    const resolved = resolveProcessTemplateInfo(inlineSummary, templateGroups);
    if (resolved?.definition_id) {
      return { ...inlineSummary, definition_id: resolved.definition_id };
    }
    return inlineSummary;
  }, [selectedMainRow, templateGroups, triage, useTriageLoad]);

  const subProcesses: ManualProcessSummary[] = selectedMainRow
    ? [...selectedMainRow.subProcesses]
    : [];

  const selectedSubProcesses = useMemo(
    () => subProcesses.filter((sp) => selectedSubIds.has(sp.id)),
    [subProcesses, selectedSubIds],
  );

  const requiresSubProcessSelection = subProcesses.length > 0;
  const hasValidSubProcessSelection =
    !requiresSubProcessSelection || selectedSubProcesses.length > 0;

  useEffect(() => {
    const definitionId = mainProcess?.definition_id?.trim();
    console.log("[ManualAssessment PreInit] dialog selection changed", {
      open,
      selectedMainId,
      mainProcessName: mainProcess?.name ?? null,
      definitionId: definitionId ?? null,
      useTriageLoad,
    });

    if (!open || !definitionId) {
      if (open && selectedMainId != null && !definitionId) {
        console.warn(
          "[ManualAssessment PreInit] skipped pre-init check: main process has no definition_id",
          {
            selectedMainId,
            mainProcessName: mainProcess?.name ?? null,
          },
        );
      }
      setRequiresInit(null);
      setPreInitFailed(false);
      setPreInitCheckPending(false);
      setPreInitTasks([]);
      setPreInitWorkflowFields([]);
      return;
    }

    let cancelled = false;
    setPreInitCheckPending(true);
    setPreInitFailed(false);
    setRequiresInit(null);
    void getManualAssessmentProcessPreInit(definitionId)
      .then((response) => {
        if (cancelled) return;
        const nextRequiresInit = Boolean(response.requiresInit);
        console.log("[ManualAssessment PreInit] button label decision", {
          definitionId,
          requiresInit: nextRequiresInit,
          initTasksCount: response.initTasks?.length ?? 0,
          workflowFieldsCount: response.workflowFields?.length ?? 0,
          primaryActionLabel: nextRequiresInit ? "Confirm" : "Initiate Processes",
        });
        setRequiresInit(nextRequiresInit);
        setPreInitFailed(false);
        setPreInitTasks((response.initTasks ?? []) as PreInitTask[]);
        setPreInitWorkflowFields(response.workflowFields ?? []);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("[ManualAssessment PreInit] pre-init check failed", {
          definitionId,
          error,
        });
        showMessage(
          resolveManualAssessmentInitiateError(
            error,
            "Failed to load process initialization. Please try again later.",
          ),
          "error",
        );
        setRequiresInit(null);
        setPreInitFailed(true);
        setPreInitTasks([]);
        setPreInitWorkflowFields([]);
      })
      .finally(() => {
        if (!cancelled) {
          setPreInitCheckPending(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    mainProcess?.definition_id,
    mainProcess?.name,
    selectedMainId,
    useTriageLoad,
    showMessage,
  ]);

  const startWorkflow = async (
    initTasksPayload?: ReturnType<typeof buildInitTasksPayload>,
    references?: WorkflowStartReference[],
  ) => {
    if (!mainProcess) {
      showMessage("Select a main process before initiating.", "error");
      return;
    }

    if (requiresSubProcessSelection && selectedSubProcesses.length === 0) {
      showMessage("Select at least one subprocess before initiating.", "error");
      return;
    }

    setSubmitting(true);

    try {
      const catalog = await getManualAssessmentProcessTemplates();
      if (!useTriageLoad) {
        setTemplateGroups(catalog);
      }

      const mainTemplate = resolveProcessTemplateInfo(mainProcess, catalog);
      if (!mainTemplate) {
        showMessage(
          "Could not resolve the main process template. Try again or contact support.",
          "error",
        );
        return;
      }

      const subTemplates: ProcessTemplateInfo[] = [];
      for (const sub of selectedSubProcesses) {
        const resolved = resolveProcessTemplateInfo(sub, catalog);
        if (!resolved) {
          showMessage(
            `Could not resolve subprocess "${sub.name}". Try again or contact support.`,
            "error",
          );
          return;
        }
        subTemplates.push(resolved);
      }

      const res = await startManualAssessmentWorkflow({
        regulation_id: regulation.regulationId,
        regulation_version: regulation.regulationVersion,
        regulation_title: regulation.regulationTitle,
        process_template: mainTemplate,
        sub_process_templates: subTemplates,
        concept_assessment_result: useTriageLoad ? conceptAssessmentResult : null,
        tenant_id: getTenantId(),
        ...(references && references.length > 0 ? { references } : {}),
        init_tasks: initTasksPayload,
      });

      if (res.status !== "success") {
        showMessage(
          formatManualAssessmentInitiateErrorMessage(
            res.message ?? "Failed to start manual assessment workflow.",
            res.code,
          ),
          "error",
        );
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: ["manual-assessment", "workflows"],
      });

      showMessage(
        MANUAL_ASSESSMENT_INITIATE_SUCCESS_TOAST,
        "success",
        MANUAL_ASSESSMENT_INITIATE_SUCCESS_TOAST_MS,
      );
      setPreInitOpen(false);
      onInitiated?.();
      onClose();
    } catch (e: unknown) {
      showMessage(
        resolveManualAssessmentInitiateError(
          e,
          "Failed to start manual assessment.",
        ),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleInitiate = async () => {
    if (!mainProcess) {
      showMessage("Select a main process before initiating.", "error");
      return;
    }

    if (preInitFailed) {
      const definitionId = mainProcess.definition_id?.trim();
      if (!definitionId) {
        showMessage(
          "Could not resolve the main process template. Try again or contact support.",
          "error",
        );
        return;
      }

      setPreInitCheckPending(true);
      let nextRequiresInit = false;
      let nextTasks: PreInitTask[] = [];
      let nextFields: PreInitWorkflowField[] = [];
      try {
        const response = await getManualAssessmentProcessPreInit(definitionId);
        nextRequiresInit = Boolean(response.requiresInit);
        nextTasks = (response.initTasks ?? []) as PreInitTask[];
        nextFields = response.workflowFields ?? [];
        console.log("[ManualAssessment PreInit] retry succeeded", {
          definitionId,
          requiresInit: nextRequiresInit,
          initTasksCount: nextTasks.length,
          workflowFieldsCount: nextFields.length,
          primaryActionLabel: nextRequiresInit
            ? "Confirm"
            : "Initiate Processes",
        });
        setRequiresInit(nextRequiresInit);
        setPreInitFailed(false);
        setPreInitTasks(nextTasks);
        setPreInitWorkflowFields(nextFields);
      } catch (error: unknown) {
        console.error("[ManualAssessment PreInit] retry failed", {
          definitionId,
          error,
        });
        showMessage(
          resolveManualAssessmentInitiateError(
            error,
            "Failed to load process initialization. Please try again later.",
          ),
          "error",
        );
        setRequiresInit(null);
        setPreInitFailed(true);
        setPreInitTasks([]);
        setPreInitWorkflowFields([]);
        return;
      } finally {
        setPreInitCheckPending(false);
      }

      if (nextRequiresInit) {
        if (nextTasks.length > 0 || nextFields.length > 0) {
          setPreInitOpen(true);
        }
        return;
      }

      await startWorkflow();
      return;
    }

    if (requiresInit) {
      if (preInitTasks.length > 0 || preInitWorkflowFields.length > 0) {
        setPreInitOpen(true);
      }
      return;
    }

    await startWorkflow();
  };

  const handlePreInitConfirm = async (formData: Record<string, unknown>) => {
    const initTasksPayload = buildInitTasksPayload(preInitTasks, formData);
    const references = buildWorkflowReferences(preInitWorkflowFields, formData);
    await startWorkflow(initTasksPayload, references);
  };

  const initiateDisabled =
    submitting ||
    loadPending ||
    preInitCheckPending ||
    loadFailed ||
    !mainProcess ||
    selectedMainId == null ||
    !hasValidSubProcessSelection;

  const primaryActionLabel = (() => {
    if (submitting) {
      return requiresInit ? "Loading…" : "Initiating…";
    }
    if (preInitCheckPending) {
      return "Checking…";
    }
    if (preInitFailed) {
      return "Retry";
    }
    return requiresInit ? "Confirm" : "Initiate Processes";
  })();

  const showEmptyProcessState =
    initialLoadComplete && !loadFailed && mainRows.length === 0;

  const showProcessTable =
    initialLoadComplete &&
    !loadFailed &&
    mainRows.length > 0 &&
    (useTriageLoad ? Boolean(triage) : true);

  return (
    <>
    <Dialog
      open={open && !preInitOpen}
      onClose={withoutBackdropClose(onClose)}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: "1080px !important",
            maxWidth: "calc(100vw - 64px)",
            maxHeight: "calc(100vh - 48px)",
            borderRadius: "12px",
            boxShadow:
              "0px 3px 6px -4px rgba(0,0,0,0.12), 0px 6px 16px 0px rgba(0,0,0,0.08), 0px 9px 28px 8px rgba(0,0,0,0.05)",
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          borderBottom: "1px solid #F3F4F6",
          px: 3,
          py: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "25px",
              color: "#0F172A",
            }}
          >
            Start Manual Assessment
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: "20px",
              color: "#64748B",
            }}
          >
            Review and select follow-up processes to start the manual assessment.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close Start Manual Assessment"
          onClick={onClose}
          disabled={submitting}
          sx={{ ...DIALOG_CLOSE_BUTTON_SX, mt: 0.25 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 4, pt: "24px !important", pb: 0 }}>
        {loadPending ? (
          <Box
            sx={{
              minHeight: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
              py: 3,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              {useTriageLoad
                ? "Loading recommended processes…"
                : "Loading process templates…"}
            </Typography>
          </Box>
        ) : null}

        {showEmptyProcessState ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              minHeight: 320,
              py: 6,
              px: 3,
            }}
          >
            <Box
              component="img"
              src={noDataProcessMapping}
              alt=""
              sx={{ width: 160, height: "auto", mb: 2 }}
            />
            <Typography
              sx={{
                color: "#8c8c8c",
                fontSize: "14px",
                lineHeight: 1.5,
                maxWidth: 480,
              }}
            >
              {MANUAL_ASSESSMENT_NO_PROCESS_TEMPLATES_MESSAGE}
            </Typography>
          </Box>
        ) : null}

        {showProcessTable ? (
          <StartManualAssessmentProcessTable
            rows={mainRows}
            selectedMainId={selectedMainId}
            onSelectedMainIdChange={setSelectedMainId}
            expandedMainIds={expandedMainIds}
            onExpandedMainIdsChange={setExpandedMainIds}
            selectedSubIds={selectedSubIds}
            onSelectedSubIdsChange={(ids) => setSelectedSubIds(new Set(ids))}
            recommendedSubProcesses={
              useTriageLoad ? (triage?.recommended_sub_processes ?? []) : []
            }
            showImpactedConceptsColumn={useTriageLoad}
            showHelperText={false}
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 4, py: 1.5, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            fontFamily: '"Nunito Sans", sans-serif',
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 400,
            px: 1.5,
            py: 1,
            minWidth: 0,
            height: 36,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={initiateDisabled}
          onClick={() => void handleInitiate()}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            fontFamily: '"Nunito Sans", sans-serif',
            fontSize: 14,
            lineHeight: "20px",
            fontWeight: 400,
            px: 1.5,
            py: 1,
            minWidth: 0,
            height: 36,
          }}
        >
          {primaryActionLabel}
        </Button>
      </DialogActions>
    </Dialog>
      <ManualAssessmentPreInitDialog
        open={preInitOpen}
        initTasks={preInitTasks}
        workflowFields={preInitWorkflowFields}
        submitting={submitting}
        onClose={() => {
          if (!submitting) setPreInitOpen(false);
        }}
        onConfirm={handlePreInitConfirm}
      />
    </>
  );
}
